// Pure geometry helpers. All coordinates are in IMAGE-SPACE pixels
// (the captured frame's intrinsic resolution), independent of CSS transforms.

export interface Point {
  x: number;
  y: number;
}

export interface Line {
  a: Point; // start
  b: Point; // end
}

export const dist = (p: Point, q: Point): number =>
  Math.hypot(p.x - q.x, p.y - q.y);

export const midpoint = (p: Point, q: Point): Point => ({
  x: (p.x + q.x) / 2,
  y: (p.y + q.y) / 2,
});

/**
 * Smallest unsigned angle (in degrees) between two lines, regardless of
 * point order on each line. Result ∈ [0, 90].
 *
 * Suitable for generic "angle between two lines" queries.
 */
export function angleBetweenLinesDeg(l1: Line, l2: Line): number {
  const v1x = l1.b.x - l1.a.x;
  const v1y = l1.b.y - l1.a.y;
  const v2x = l2.b.x - l2.a.x;
  const v2y = l2.b.y - l2.a.y;

  const n1 = Math.hypot(v1x, v1y);
  const n2 = Math.hypot(v2x, v2y);
  if (n1 === 0 || n2 === 0) return 0;

  const cos = (v1x * v2x + v1y * v2y) / (n1 * n2);
  const clamped = Math.max(-1, Math.min(1, cos));
  let deg = (Math.acos(clamped) * 180) / Math.PI;
  if (deg > 90) deg = 180 - deg; // unsigned acute/right angle
  return deg;
}

/**
 * Angle between two vectors sharing a common vertex.
 *
 * Result ∈ [0, 180]. Useful when the user wants the *opening angle* of
 * an osteotomy wedge (vertex at apex, two limbs as oriented vectors).
 *
 * `vertex` is the apex; `arm1` and `arm2` are the line endpoints.
 */
export function openingAngleDeg(
  vertex: Point,
  arm1: Point,
  arm2: Point,
): number {
  const v1x = arm1.x - vertex.x;
  const v1y = arm1.y - vertex.y;
  const v2x = arm2.x - vertex.x;
  const v2y = arm2.y - vertex.y;

  const n1 = Math.hypot(v1x, v1y);
  const n2 = Math.hypot(v2x, v2y);
  if (n1 === 0 || n2 === 0) return 0;

  const cos = (v1x * v2x + v1y * v2y) / (n1 * n2);
  return (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI;
}

/**
 * Intersection point of two infinite lines (each defined by two points).
 * Returns null when the lines are parallel.
 */
export function lineLineIntersection(l1: Line, l2: Line): Point | null {
  const { a: p1, b: p2 } = l1;
  const { a: p3, b: p4 } = l2;
  const denom = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
  if (Math.abs(denom) < 1e-9) return null;
  const t =
    ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / denom;
  return {
    x: p1.x + t * (p2.x - p1.x),
    y: p1.y + t * (p2.y - p1.y),
  };
}

/** Format a degree value for display. */
export const fmtDeg = (deg: number, frac = 1): string =>
  `${deg.toFixed(frac)}°`;

/** Format a length in mm for display. */
export const fmtMm = (mm: number, frac = 1): string => `${mm.toFixed(frac)} mm`;
